import {
  applySnapshot,
  flow,
  getRoot,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { getRights } from 'utils';
import {
  dashboardAdminSectionPermissions,
  dashboardSectionPermissions,
  sortDashboardSectionsByVisits,
  validateDashboardSections,
} from './utils';
import { Dashboard, NewNews } from 'http-requests';
import { DashboardSections } from './models';
import { RequestStateModel } from '../general-models';
import type { IUserStore } from '../userStore';

import type {
  DashboardAdminSectionKey,
  DashboardAdoption,
  DashboardSectionGroups,
  DashboardSectionWithAccess,
} from './types';

export const DashboardStore = types
  .model('DashboardStore', {
    dashboardSections: types.optional(DashboardSections, {}),
    systemNews: types.optional(
      types.array(types.frozen<NewNews.NewsFeedDTO>()),
      []
    ),
    workerDashboardStats: types.optional(types.map(types.number), {}),
    providerBusinessTypePermissions: types.optional(
      types.map(types.string),
      {}
    ),
    providerPermissions: types.optional(types.map(types.string), {}),
    statuses: types.optional(
      types.model('DashboardStoreStatuses', {
        getDashboardSections: types.optional(RequestStateModel, {}),
        getSystemNews: types.optional(RequestStateModel, {}),
        getProviderBusinessTypePermissions: types.optional(
          RequestStateModel,
          {}
        ),
        getProviderPermissions: types.optional(RequestStateModel, {}),
        getWorkerDashboardStats: types.optional(RequestStateModel, {}),
      }),
      {}
    ),
  })
  .views((self) => ({
    get dashboardSectionsWithAccess(): DashboardSectionWithAccess[] {
      const { user } = getRoot<{ user: IUserStore }>(self);

      // Готовность данных и ошибки определяются по statuses.
      if (
        !self.statuses.getDashboardSections.fulfilled ||
        !self.statuses.getProviderBusinessTypePermissions.fulfilled ||
        !self.statuses.getProviderPermissions.fulfilled ||
        !user.statuses.permissions.fulfilled
      ) {
        return [];
      }

      return dashboardSectionPermissions.map(
        ({ slug, providerSlugs, userSlugs }) => ({
          slug,
          title: self.dashboardSections[slug],
          isAllowedByBusinessType: providerSlugs.some((permissionSlug) =>
            self.providerBusinessTypePermissions
              .get(permissionSlug)
              ?.includes('R')
          ),
          isConnected: providerSlugs.some((permissionSlug) =>
            self.providerPermissions.get(permissionSlug)?.includes('R')
          ),
          canRead: userSlugs.some(
            (permissionSlug) => getRights(permissionSlug, user.permissions).read
          ),
        })
      );
    },
  }))
  .views((self) => ({
    get dashboardSectionGroups(): DashboardSectionGroups {
      const { user } = getRoot<{ user: IUserStore }>(self);
      const groups: DashboardSectionGroups = {
        frequent: [],
        connected: [],
        available: [],
      };

      // Итоговых прав достаточно для Frequent, пока данные организации недоступны.
      if (
        self.statuses.getDashboardSections.fulfilled &&
        user.statuses.permissions.fulfilled &&
        (!self.statuses.getProviderBusinessTypePermissions.fulfilled ||
          !self.statuses.getProviderPermissions.fulfilled)
      ) {
        groups.frequent = dashboardSectionPermissions
          .filter(({ userSlugs }) =>
            userSlugs.some((slug) => getRights(slug, user.permissions).read)
          )
          .map(({ slug }) => slug);
      }

      for (const section of self.dashboardSectionsWithAccess) {
        if (!section.isAllowedByBusinessType) {
          continue;
        }

        if (!section.isConnected) {
          groups.available.push(section.slug);
        } else if (section.canRead) {
          groups.frequent.push(section.slug);
        } else if (user.type === 'Worker') {
          groups.connected.push(section.slug);
        }
      }

      if (
        user.type === 'Worker' &&
        self.statuses.getWorkerDashboardStats.fulfilled
      ) {
        groups.frequent = sortDashboardSectionsByVisits(
          groups.frequent,
          self.workerDashboardStats
        );
      }

      return groups;
    },
    get dashboardAdoption(): DashboardAdoption {
      let totalSections = 0;
      let connectedSections = 0;

      for (const section of self.dashboardSectionsWithAccess) {
        if (!section.isAllowedByBusinessType) {
          continue;
        }

        totalSections += 1;

        if (section.isConnected) {
          connectedSections += 1;
        }
      }

      return {
        connectedSections,
        totalSections,
        percent: totalSections
          ? Math.round((100 * connectedSections) / totalSections)
          : 0,
      };
    },
    get dashboardAdminSections(): DashboardAdminSectionKey[] {
      const { user } = getRoot<{ user: IUserStore }>(self);

      if (!user.statuses.permissions.fulfilled) {
        return [];
      }

      return dashboardAdminSectionPermissions
        .filter(({ userSlugs }) =>
          userSlugs.some((slug) => getRights(slug, user.permissions).read)
        )
        .map(({ slug }) => slug);
    },
  }))
  .actions((self) => ({
    getDashboardSections: flow(function* () {
      if (self.statuses.getDashboardSections.pending) {
        return;
      }

      try {
        self.statuses.getDashboardSections.loadingStates();

        const response: SnapshotIn<typeof DashboardSections> =
          yield Dashboard.GetDashboardSections();

        validateDashboardSections(response);
        applySnapshot(self.dashboardSections, response);

        self.statuses.getDashboardSections.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getDashboardSections.errorStates();
      } finally {
        self.statuses.getDashboardSections.finallyStates();
      }
    }),
    getSystemNews: flow(function* () {
      if (self.statuses.getSystemNews.pending) {
        return;
      }

      try {
        self.statuses.getSystemNews.loadingStates();
        self.systemNews.clear();

        const response: NewNews.GetNewsResponseDTO = yield NewNews.GetNews({
          params: {
            limit: 5,
            offset: 0,
            state: 'published',
            type__in: ['news'],
            category__in: [
              'release',
              'good_know',
              'sys_offer',
              'sys_notify',
              'tech_works',
            ],
          },
        });
        applySnapshot(self.systemNews, response.results);

        self.statuses.getSystemNews.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getSystemNews.errorStates();
      } finally {
        self.statuses.getSystemNews.finallyStates();
      }
    }),
    getProviderBusinessTypePermissions: flow(function* () {
      if (self.statuses.getProviderBusinessTypePermissions.pending) {
        return;
      }

      try {
        self.statuses.getProviderBusinessTypePermissions.loadingStates();

        const response: Dashboard.ProviderBusinessTypePermissionsDTO =
          yield Dashboard.GetProviderBusinessTypePermissions();
        applySnapshot(self.providerBusinessTypePermissions, response);

        self.statuses.getProviderBusinessTypePermissions.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getProviderBusinessTypePermissions.errorStates();
      } finally {
        self.statuses.getProviderBusinessTypePermissions.finallyStates();
      }
    }),
    getProviderPermissions: flow(function* (providerId: string) {
      if (!providerId || self.statuses.getProviderPermissions.pending) {
        return;
      }

      try {
        self.statuses.getProviderPermissions.loadingStates();
        self.providerPermissions.clear();

        const response: Dashboard.ProviderPermissionsDTO =
          yield Dashboard.GetProviderPermissions(providerId);
        applySnapshot(self.providerPermissions, response);

        self.statuses.getProviderPermissions.successStates();
      } catch (e) {
        console.error(e);
        self.statuses.getProviderPermissions.errorStates();
      } finally {
        self.statuses.getProviderPermissions.finallyStates();
      }
    }),
    getWorkerDashboardStats: flow(function* () {
      if (self.statuses.getWorkerDashboardStats.pending) return;

      try {
        self.statuses.getWorkerDashboardStats.loadingStates();

        const response: Dashboard.WorkerDashboardStatsDTO =
          yield Dashboard.GetWorkerDashboardStats();
        applySnapshot(self.workerDashboardStats, response);

        self.statuses.getWorkerDashboardStats.successStates();
      } catch (e) {
        console.error(e);
        // Очищаем старые счётчики для возврата к базовому порядку при ошибке.
        self.workerDashboardStats.clear();
        self.statuses.getWorkerDashboardStats.errorStates();
      } finally {
        self.statuses.getWorkerDashboardStats.finallyStates();
      }
    }),
    createVisitStat: flow(function* (section: string) {
      try {
        yield Dashboard.CreateVisitStat({ section });
      } catch (e) {
        console.error(e);
        // Ошибка фоновой статистики не должна влиять на работу раздела.
      }
    }),
  }));
